import { useQuery } from "@tanstack/react-query";
import { getPosts } from "@/utils/api";
import { QueryKeys } from "@/utils/tanstack";
import { SchemaLoader } from "./ui/SchemaLoader";
import { CardDetails } from "./card-details";
import { useBookmarks } from "@/hooks/useBookmarks";
import type { TPost } from "@/utils/validators";

function getUserProfileUrl(post: TPost) {
  const twitterUsername = post.user.twitter_username?.trim();
  if (twitterUsername) return `https://twitter.com/${twitterUsername}`;
  const githubUsername = post.user.github_username?.trim();
  if (githubUsername) return `https://github.com/${githubUsername}`;
  const websiteUrl = post.user.website_url?.trim();
  if (websiteUrl) return websiteUrl;
  const devTo = post.user.username?.trim();
  if (devTo) return `https://dev.to/${devTo}`;

  return null;
}

export function Posts({
  showBookmarks,
  tag,
}: {
  showBookmarks: boolean;
  tag: string;
}) {
  const {
    data: posts,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryFn: async () => {
      const posts = await getPosts({ tag });
      return posts.map((post) => ({
        ...post,
        user: {
          profileUrl: getUserProfileUrl(post),
          ...post.user,
        },
      }));
    },
    queryKey: [QueryKeys.GET_POSTS_QUERY],
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    enabled: !showBookmarks,
  });
  const { bookmarkedPosts } = useBookmarks("Posts");

  if (isError) {
    return (
      <div className="rounded-lg border border-gray-700 bg-[#1a1a1a] p-6 text-center">
        <h2 className="text-2xl font-bold text-red-500">
          An error occurred: {(error as Error).message}
        </h2>
        <p className="text-gray-300">Please try again later.</p>
      </div>
    );
  }

  const postsToDisplay = showBookmarks ? bookmarkedPosts : posts;

  if (postsToDisplay?.length === 0) {
    return (
      <div className="rounded-lg border border-gray-700 bg-[#1a1a1a] p-6 text-center">
        <h2 className="text-2xl font-bold text-gray-300">
          {showBookmarks
            ? "No bookmarked posts yet."
            : `No posts found with the '${tag}' tag.`}
        </h2>
        <p className="text-gray-500">
          {showBookmarks
            ? "Bookmark some posts to see them here."
            : "Try refreshing or check back later for new posts."}
        </p>
      </div>
    );
  }

  return (
    <section className="mt-4 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
      {isLoading
        ? Array.from({ length: 3 }).map((_, i) => <SchemaLoader key={i} />)
        : postsToDisplay?.map((post) => (
            <CardDetails key={post.id} post={post} />
          ))}
    </section>
  );
}
